var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { Calendar, CalendarCell, } from '@progress/kendo-react-dateinputs';
import { Popover } from '../Popover';
import { Icon } from '../Icon';
import { keys } from '../../utilities/keyCodes';
import { InputDateMask } from './InputDateMask';
import { TimezoneContext } from '../../utilities/TimezoneProvider';
import { FormFieldContext } from '../../components/FormField';
import { toDateObj, toTimeZone, fromTimeZone, toFormat, compareObj, invalidDate, isValidDate, } from './utils/utils';
export const DatePicker = React.forwardRef((props, ref) => {
    const { disabled, calendar: CustomCalendar, direction = 'br', error, maskChar, maskProps, maxDate, minDate, isoFormat, input: inputProps, onBlur, onChange, onOpenChange, open, placeholder, required, size, trigger, value, portalDataAttributes } = props, rest = __rest(props, ["disabled", "calendar", "direction", "error", "maskChar", "maskProps", "maxDate", "minDate", "isoFormat", "input", "onBlur", "onChange", "onOpenChange", "open", "placeholder", "required", "size", "trigger", "value", "portalDataAttributes"]);
    const tz = useContext(TimezoneContext);
    const minTZ = toTimeZone(minDate, tz); // In timezone (with shift)
    const maxTZ = toTimeZone(maxDate, tz); // In timezone (with shift)
    const todayTZ = toTimeZone(new Date(), tz, { startOfDay: true }); // In timezone (with shift)
    const { labelFor: inputId } = React.useContext(FormFieldContext);
    // State
    const [date, setDate] = React.useState(toDateObj({ date: value, tz }));
    const [showCalendar, setShowCalendar] = React.useState(open);
    // Refs for focusing
    const calendarRef = useRef(null);
    const inputRef = useRef(null);
    // Effects
    useEffect(() => open && openCalendar(), [open]); // eslint-disable-line
    useEffect(() => {
        const next = toDateObj({ date: value, tz });
        if (date.isEqualDate(next))
            return;
        setDate(next);
        onChangeReturn(next);
    }, [compareObj(value), isoFormat, tz]); // eslint-disable-line
    // Handlers
    const openCalendar = () => {
        !showCalendar && setShowCalendar(true);
        !showCalendar && (onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(true));
    };
    const closeCalendar = (e) => {
        var _a, _b;
        if ((_b = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.closest) === null || _b === void 0 ? void 0 : _b.call(_a, `[for="${inputId}"]`))
            return;
        showCalendar && setShowCalendar(false);
        showCalendar && (onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false));
    };
    const toggleCalendar = (e) => showCalendar ? closeCalendar(e) : openCalendar();
    const handleKeyDown = (e) => {
        var _a, _b;
        const { key } = e;
        if (key === keys.esc) {
            e.preventDefault();
            closeCalendar(e);
            !trigger && ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus());
        }
        if ([keys.space, keys.down].includes(key)) {
            e.preventDefault();
            openCalendar();
            (_b = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
        if (showCalendar && key === keys.tab)
            closeCalendar(e);
    };
    const handleCalendarChange = (e) => {
        var _a, _b, _c, _d;
        let calendarDate = fromTimeZone((_a = e.target) === null || _a === void 0 ? void 0 : _a.value, tz); // Local timezone
        if (((_c = (_b = e.nativeEvent) === null || _b === void 0 ? void 0 : _b.target) === null || _c === void 0 ? void 0 : _c.className) === 'k-nav-today')
            calendarDate = fromTimeZone(todayTZ, tz); // Local timezone
        const next = toDateObj({ date: calendarDate, tz });
        if (!date.isEqual(next)) {
            e.syntheticEvent.stopPropagation(); // Keeps focus on input after click on calendar
            setDate(next);
            onChangeReturn(next);
        }
        closeCalendar(e);
        !trigger && ((_d = inputRef.current) === null || _d === void 0 ? void 0 : _d.focus()); // Sets focus back on enter keypress on calendar's date
    };
    const handleMaskChange = (val, data) => {
        const next = toDateObj({
            date: val ? fromTimeZone(val, tz) : val,
            tz,
            invalidReason: data === null || data === void 0 ? void 0 : data.invalidReason,
        });
        if (!date.isEqual(next)) {
            setDate(next);
            onChangeReturn(next);
        }
    };
    const onChangeReturn = (val) => onChange === null || onChange === void 0 ? void 0 : onChange(getReturnDate(val), val);
    const getReturnDate = (val) => {
        var _a;
        if (!val || !val.dateTime)
            return undefined; // Empty input
        if (!val.isValid)
            return isoFormat ? invalidDate.toString() : invalidDate; // Invalid Date
        return isoFormat ? (_a = val.toISO) === null || _a === void 0 ? void 0 : _a.call(val) : val.toJSDate(); // Good value
    };
    const handleMaskBlur = (val, data) => {
        const current = toDateObj({
            date: val ? fromTimeZone(val, tz) : val,
            tz,
            invalidReason: data === null || data === void 0 ? void 0 : data.invalidReason,
        });
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(getReturnDate(current), current);
    };
    // Internal components
    const maskValue = () => {
        if (typeof (date === null || date === void 0 ? void 0 : date.dateTime) === 'undefined')
            return undefined; // No entry (empty)
        // Entry is valid and within min-max
        if (date === null || date === void 0 ? void 0 : date.isValid)
            return toTimeZone(date.toJSDate(), tz); // In timezone (with shift)
        if (!(date === null || date === void 0 ? void 0 : date.isValid) || (date === null || date === void 0 ? void 0 : date.invalidReason))
            return invalidDate; // Entry is not full or out of min-max range
        console.warn('@servicetitan/design-system: Something wrong with DatePicker, dateObj received:', date);
        return undefined;
    };
    const inputMaskProps = Object.assign({ disabled,
        error,
        maskChar, maxDate: maxTZ, minDate: minTZ, onBlur: handleMaskBlur, onChange: handleMaskChange, placeholder,
        required,
        size, value: maskValue(), input: inputProps }, maskProps);
    // custom cell to override the today highlight to match timezone today
    const customCell = (props) => {
        if (toFormat(props.value) === toFormat(todayTZ)) {
            return React.createElement(CalendarCell, Object.assign({}, props, { isToday: true }));
        }
        return React.createElement(CalendarCell, Object.assign({}, props, { isToday: false }));
    };
    const calendarProps = {
        value: date.isValid
            ? date.toJSDate() // Local timezone
            : null,
        max: isValidDate(maxDate) ? maxDate : undefined,
        min: isValidDate(minDate) ? minDate : undefined,
        onChange: handleCalendarChange,
        ref: calendarRef,
        cell: customCell,
    };
    const customTrigger = trigger === null || trigger === void 0 ? void 0 : trigger({
        value: date.isValid
            ? date.toJSDate() // Local timezone
            : null,
        onClick: toggleCalendar,
        open: showCalendar,
        error,
    });
    const inputMask = (React.createElement(InputDateMask, Object.assign({}, inputMaskProps, { id: inputId, inputRef: inputRef, calendarRef: calendarRef, className: "DatePicker__input", onClick: openCalendar, shortLabel: React.createElement(Icon, { name: "event", className: "DatePicker__icon", onClick: toggleCalendar, "aria-label": "toggle calendar" }), fluid: true, "data-anvil-component": "DatePicker" })));
    const DatePickerClasses = classnames('DatePicker', {
        'DatePicker--disabled': disabled,
        'DatePicker--open': showCalendar,
        [`DatePicker--${size}`]: size,
    });
    return (React.createElement(Popover, Object.assign({ portal: true, wrapperClassName: DatePickerClasses, open: showCalendar, popoverContentClassName: "DatePicker__calendar", onKeyDown: handleKeyDown, direction: direction, padding: null, width: "auto", ref: ref, onClickOutside: closeCalendar, trigger: customTrigger !== null && customTrigger !== void 0 ? customTrigger : inputMask, portalDataAttributes: portalDataAttributes }, rest), CustomCalendar ? (React.createElement(CustomCalendar, Object.assign({}, calendarProps))) : (React.createElement(Calendar, Object.assign({}, calendarProps)))));
});
//# sourceMappingURL=DatePicker.js.map